import { useState } from "react";
import { useNavigate, useLocation, Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { API, fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const inputCls = "w-full bg-ink border border-[#262626] px-4 py-3 text-sm outline-none focus:border-gold transition-colors";

export default function Auth() {
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = mode === "login" ? await login(email, password) : await register(name, email, password);
      toast.success(`Welcome${user.name ? `, ${user.name.split(" ")[0]}` : ""}.`);
      navigate(location.state?.from || (user.role === "admin" ? "/admin" : "/"));
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-20" data-testid="auth-page">
      <img src="/logo.png" alt="Dressedin" className="h-16 w-16 object-contain mb-6" />
      <h1 className="font-display font-black text-3xl tracking-tighter">
        {mode === "login" ? "Welcome back." : "Join the family."}
      </h1>
      <p className="text-sm text-muted-foreground mt-2 mb-8">Wear your identity — in and out of the app.</p>

      <div className="flex border border-[#262626] mb-8">
        {["login", "register"].map((m) => (
          <button key={m} onClick={() => setMode(m)} data-testid={`auth-tab-${m}`}
            className={`flex-1 py-3 text-xs uppercase tracking-[0.2em] font-bold transition-colors ${mode === m ? "bg-gold text-ink" : "text-muted-foreground"}`}>
            {m === "login" ? "Login" : "Sign up"}
          </button>
        ))}
      </div>

      <form onSubmit={submit} className="space-y-4">
        {mode === "register" && (
          <input data-testid="auth-name" value={name} onChange={(e) => setName(e.target.value)} required placeholder="Full name" className={inputCls} />
        )}
        <input data-testid="auth-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="Email" className={inputCls} />
        <input data-testid="auth-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} placeholder="Password (min 6 chars)" className={inputCls} />
        <button data-testid="auth-submit-btn" disabled={loading}
          className="btn-gold w-full py-4 font-display font-bold text-sm uppercase tracking-[0.2em] disabled:opacity-50">
          {loading ? "Hold on..." : mode === "login" ? "Login" : "Create account"}
        </button>
      </form>
      {mode === "login" && (
        <Link to="/forgot-password" className="text-xs text-muted-foreground hover:text-gold transition-colors mt-4 inline-block" data-testid="forgot-password-link">
          Forgot password?
        </Link>
      )}
    </div>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await API.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) { toast.error(fmtErr(err)); }
  };
  return (
    <div className="max-w-md mx-auto px-4 py-20" data-testid="forgot-password-page">
      <h1 className="font-display font-black text-3xl tracking-tighter mb-6">Reset password</h1>
      {sent ? (
        <p className="text-sm text-muted-foreground" data-testid="reset-sent-msg">
          If that email exists, a reset link is on its way. Check your inbox (and spam).
        </p>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <input data-testid="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="Your email" className={inputCls} />
          <button data-testid="forgot-submit-btn" className="btn-gold w-full py-4 font-display font-bold text-sm uppercase tracking-[0.2em]">Send reset link</button>
        </form>
      )}
    </div>
  );
}

export function ResetPassword() {
  const { token } = useParams();
  const [password, setPassword] = useState("");
  const [done, setDone] = useState(false);
  const navigate = useNavigate();
  const submit = async (e) => {
    e.preventDefault();
    try {
      await API.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password updated");
      setTimeout(() => navigate("/auth"), 1500);
    } catch (err) { toast.error(fmtErr(err)); }
  };
  return (
    <div className="max-w-md mx-auto px-4 py-20" data-testid="reset-password-page">
      <h1 className="font-display font-black text-3xl tracking-tighter mb-6">New password</h1>
      {done ? (
        <p className="text-gold text-sm">Done. Redirecting to login...</p>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <input data-testid="reset-new-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            required minLength={6} placeholder="New password (min 6 chars)" className={inputCls} />
          <button data-testid="reset-submit-btn" className="btn-gold w-full py-4 font-display font-bold text-sm uppercase tracking-[0.2em]">Update password</button>
        </form>
      )}
    </div>
  );
}
